import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import excelModalApi from '../../../../services/excelModalApi';

export const useExcelData = (editingFile, currentFY, scenario) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [originalData, setOriginalData] = useState({});
  const [editedCells, setEditedCells] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentFileId, setCurrentFileId] = useState(null);
  const [sourceFiles, setSourceFiles] = useState([]);

  const [files, setFiles] = useState([]);
  const [fileRowRanges, setFileRowRanges] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
    const [fileId, setFileId] = useState('');


  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [showRejectedWarning, setShowRejectedWarning] = useState(false);
  const [pendingFileToEdit, setPendingFileToEdit] = useState(null);

  const getFileTypeId = useCallback(() => {
    if (!editingFile) return null;
    if (typeof editingFile === 'string') return editingFile;
    return editingFile.fileType || editingFile._id || null;
  }, [editingFile]);

  const loadExcelData = useCallback(
    async (talukaIds, month) => {
      const fileTypeId = getFileTypeId();

      if (!fileTypeId || !currentFY) return null;

      const isTalukaScenario = scenario?.includes('taluka') || scenario?.toLowerCase().includes('sdo');

      if (isTalukaScenario && (!talukaIds || talukaIds.length === 0)) {
        setHeaders([]);
        setRows([]);
        setSourceFiles([]);
        setFiles([]);
        setFileRowRanges([]);
        setFileStatuses({});
        return null;
      }

      setLoading(true);

      setIsEditMode(false);
      setEditingFileId(null);
      setShowRejectedWarning(false);
      setPendingFileToEdit(null);

      try {
        const params = { month };

        if (isTalukaScenario) {
          if (scenario?.toLowerCase().includes('sdo')) {
            if (talukaIds.length === 1) {
              params.userId = talukaIds[0];
              params.sdoId = talukaIds[0];
            } else {
              params.userIds = talukaIds.join(',');
            }
          } else {
            if (talukaIds.length === 1) {
              params.tahsilId = talukaIds[0];
            } else {
              params.tahsilIds = talukaIds;
            }
          }
        }


        const result = await excelModalApi.getExcelData(
          fileTypeId,
          currentFY,
          params,
        );
        setFileId(result?.excelData?.fileTypeId || '');        

        if (result.success && result.excelData) {
          const excel = result.excelData;

          setHeaders(excel.headers || []);
          setRows(excel.rows || []);

          let filesList = [];
          if (result.files && Array.isArray(result.files)) {
            filesList = result.files;
          } else if (result.data?.files && Array.isArray(result.data.files)) {
            filesList = result.data.files;
          } else if (result.data?.file) {
            filesList = [result.data.file];
          }

          filesList = filesList.map((file) => ({
            ...file,
            status: file.status || 'submitted',
          }));

          setFiles(filesList);
          setSourceFiles(filesList);

          const statusMap = {};
          filesList.forEach((file) => {
            statusMap[file._id] = file.status || 'submitted';
          });
          setFileStatuses(statusMap);

          if (isTalukaScenario && excel.sources && excel.sources.length > 0) {
            const ranges = [];
            let startRow = 0;
            excel.sources.forEach((source) => {
              const endRow = startRow + source.rowCount - 1;
              ranges.push({
                fileId: source.fileId,
                fileName: source.tahsilName || source.sdoName || source.name || 'File',
                startRow,
                endRow,
                rowCount: source.rowCount,
                status: source.status || 'submitted',
              });
              startRow = endRow + 1;
            });
            setFileRowRanges(ranges);
          } else if (filesList.length === 1) {
            setFileRowRanges([
              {
                fileId: filesList[0]._id,
                fileName: filesList[0].tahsil?.name || filesList[0].sdo?.name || filesList[0].name || 'File',
                startRow: 0,
                endRow: excel.rows.length - 1,
                rowCount: excel.rows.length,
                status: filesList[0].status || 'submitted',
              },
            ]);
          }

          const original = {};
          if (excel.rows) {
            excel.rows.forEach((row, rowIdx) => {
              row.forEach((cell, colIdx) => {
                original[`${rowIdx}-${colIdx}`] = cell;
              });
            });
          }
          setOriginalData(original);
          setEditedCells({});
          setHasUnsavedChanges(false);

          if (filesList.length > 0) {
            setCurrentFileId(filesList[0]?._id || null);
          }

          return result;
        } else {
          toast({
            title: 'No data found',
            description: result.error || 'No data available',
            status: 'info',
            duration: 3000,
            position: 'top-right',
          });
          setHeaders([]);
          setRows([]);
          setSourceFiles([]);
          setFiles([]);
          setFileRowRanges([]);
          setFileStatuses({});
          return null;
        }
      } catch (error) {
        toast({
          title: 'Error loading data',
          description: error.message,
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getFileTypeId, currentFY, toast, scenario],
  );

  const startEditing = useCallback(
    (fileId) => {
      if (hasUnsavedChanges) {
        toast({
          title: 'Unsaved changes',
          description: 'Please save or discard current changes first',
          status: 'warning',
          duration: 3000,
          position: 'top-right',
        });
        return false;
      }

      const fileToEdit =
        files.find((f) => f._id === fileId) ||
        sourceFiles.find((f) => f._id === fileId);

      if (!fileToEdit) {
        toast({
          title: 'Error',
          description: 'Record not found',
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        return false;
      }

      if (
        fileToEdit.status === 'rejected' ||
        fileStatuses[fileId] === 'rejected'
      ) {
        setPendingFileToEdit(fileId);
        setShowRejectedWarning(true);
        return false;
      }

      proceedWithEditing(fileId);
      return true;
    },
    [hasUnsavedChanges, files, sourceFiles, fileStatuses, toast],
  );

  const proceedWithEditing = useCallback(
    (fileId) => {
      setIsEditMode(true);
      setEditingFileId(fileId);
      setShowRejectedWarning(false);
      setPendingFileToEdit(null);

      toast({
        title: 'Edit mode activated',
        description:
          fileStatuses[fileId] === 'rejected'
            ? 'This rejected record will be resubmitted for approval after saving'
            : 'You can now edit this record',
        status: 'info',
        duration: 3000,
        position: 'top-right',
      });
    },
    [fileStatuses, toast],
  );

  const cancelRejectedEdit = useCallback(() => {
    setShowRejectedWarning(false);
    setPendingFileToEdit(null);
  }, []);

  const cancelEditing = useCallback(() => {
    if (hasUnsavedChanges) {
      setRows((prevRows) => {
        const newRows = [...prevRows];
        Object.entries(editedCells).forEach(([key]) => {
          const [rowIdx, colIdx] = key.split('-').map(Number);
          if (newRows[rowIdx]) {
            const originalValue = originalData[key] || '';
            const newRow = [...newRows[rowIdx]];
            newRow[colIdx] = originalValue;
            newRows[rowIdx] = newRow;
          }
        });
        return newRows;
      });

      setEditedCells({});
      setHasUnsavedChanges(false);
    }

    setIsEditMode(false);
    setEditingFileId(null);

    toast({
      title: 'Editing cancelled',
      status: 'info',
      duration: 2000,
      position: 'top-right',
    });
  }, [hasUnsavedChanges, editedCells, originalData, toast]);

  const handleCellEdit = useCallback(
    (rowIndex, colIndex, value) => {
      if (!isEditMode) return;

      if (rowIndex < 0) return;

      const cellKey = `${rowIndex}-${colIndex}`;
      const originalValue = originalData[cellKey] || '';

      setEditedCells((prev) => {
        const newEdited = { ...prev };

        if (value !== originalValue) {
          newEdited[cellKey] = value;
        } else {
          delete newEdited[cellKey];
        }

        setHasUnsavedChanges(Object.keys(newEdited).length > 0);
        return newEdited;
      });

      setRows((prevRows) => {
        const newRows = [...prevRows];
        if (!newRows[rowIndex]) return newRows;

        const newRow = [...newRows[rowIndex]];
        newRow[colIndex] = value;
        newRows[rowIndex] = newRow;
        return newRows;
      });
    },
    [isEditMode, originalData],
  );

  const saveChanges = useCallback(async () => {
    if (!editingFileId || Object.keys(editedCells).length === 0) {
      toast({
        title: 'No changes',
        description: 'No changes to save',
        status: 'info',
        duration: 2000,
        position: 'top-right',
      });
      return null;
    }

    setLoading(true);

    try {
      const fileInfo = fileRowRanges.find(
        (range) => range.fileId === editingFileId,
      );

      if (!fileInfo) {
        toast({
          title: 'Error',
          description: 'Record information not found',
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        setLoading(false);
        return null;
      }

      const fileEditedCells = Object.entries(editedCells).filter(([key]) => {
        const [rowIndex] = key.split('-').map(Number);
        return rowIndex >= fileInfo.startRow && rowIndex <= fileInfo.endRow;
      });

      if (fileEditedCells.length === 0) {
        toast({
          title: 'No changes',
          description: 'No changes in the current Record',
          status: 'info',
          duration: 2000,
          position: 'top-right',
        });
        setLoading(false);
        return null;
      }

      const updates = fileEditedCells.map(([key, value]) => {
        const [absoluteRowIndex, colIndex] = key.split('-').map(Number);
        const relativeRowIndex = absoluteRowIndex - fileInfo.startRow;

        return {
          rowIndex: relativeRowIndex + 1,
          columnIndex: colIndex,
          value,
        };
      });

      const result = await excelModalApi.saveExcelData(
        editingFileId,
        updates,
        'Sheet1',
      );

      if (result.success) {
        const wasRejected = fileStatuses[editingFileId] === 'rejected';

        setFileStatuses((prev) => ({
          ...prev,
          [editingFileId]: 'submitted',
        }));

        setFiles((prev) =>
          prev.map((file) =>
            file._id === editingFileId
              ? { ...file, status: 'submitted' }
              : file,
          ),
        );

        setFileRowRanges((prev) =>
          prev.map((range) =>
            range.fileId === editingFileId
              ? { ...range, status: 'submitted' }
              : range,
          ),
        );

        toast({
          title: 'Success',
          description: wasRejected
            ? 'Record saved and resubmitted for approval'
            : result.message || 'Changes saved successfully',
          status: 'success',
          duration: 3000,
          position: 'top-right',
        });

        setOriginalData((prev) => {
          const newOriginal = { ...prev };
          fileEditedCells.forEach(([key, value]) => {
            newOriginal[key] = value;
          });
          return newOriginal;
        });

        setEditedCells((prev) => {
          const newEdited = { ...prev };
          fileEditedCells.forEach(([key]) => {
            delete newEdited[key];
          });
          return newEdited;
        });

        setHasUnsavedChanges(
          Object.keys(editedCells).length - fileEditedCells.length > 0,
        );

        if (Object.keys(editedCells).length - fileEditedCells.length === 0) {
          setIsEditMode(false);
          setEditingFileId(null);
        }

        return result;
      } else {
        toast({
          title: 'Error saving',
          description: result.error,
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        position: 'top-right',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [editingFileId, editedCells, fileStatuses, fileRowRanges, toast]);

  const discardChanges = useCallback(() => {
    if (!hasUnsavedChanges) return;

    setRows((prevRows) => {
      const newRows = [...prevRows];
      Object.entries(editedCells).forEach(([key]) => {
        const [rowIdx, colIdx] = key.split('-').map(Number);
        if (newRows[rowIdx]) {
          const originalValue = originalData[key] || '';
          const newRow = [...newRows[rowIdx]];
          newRow[colIdx] = originalValue;
          newRows[rowIdx] = newRow;
        }
      });
      return newRows;
    });

    setEditedCells({});
    setHasUnsavedChanges(false);
    setIsEditMode(false);
    setEditingFileId(null);

    toast({
      title: 'Changes discarded',
      status: 'info',
      duration: 2000,
      position: 'top-right',
    });
  }, [editedCells, originalData, hasUnsavedChanges, toast]);

  const resetData = useCallback(() => {
    setHeaders([]);
    setRows([]);
    setOriginalData({});
    setEditedCells({});
    setHasUnsavedChanges(false);
    setCurrentFileId(null);
    setSourceFiles([]);
    setFiles([]);
    setFileRowRanges([]);
    setFileStatuses({});
    setIsEditMode(false);
    setEditingFileId(null);
    setShowRejectedWarning(false);
    setPendingFileToEdit(null);
  }, []);

  const isFileEditing = useCallback(
    (fileId) => {
      return isEditMode && editingFileId === fileId;
    },
    [isEditMode, editingFileId],
  );

  const isAnyFileEditing = useCallback(() => {
    return isEditMode && editingFileId !== null;
  }, [isEditMode, editingFileId]);

  const isCellEdited = useCallback(
    (rowIndex, colIndex) => {
      const cellKey = `${rowIndex}-${colIndex}`;
      return editedCells.hasOwnProperty(cellKey);
    },
    [editedCells],
  );

  const getFileStatus = useCallback(
    (fileId) => {
      return fileStatuses[fileId] || 'submitted';
    },
    [fileStatuses],
  );

  return {
    loading,
    headers,
    rows,
    hasUnsavedChanges,
    currentFileId,
    sourceFiles,
    files,
    fileId,
    fileRowRanges,
    fileStatuses,
    isEditMode,
    editingFileId,
    showRejectedWarning,
    pendingFileToEdit,
    loadExcelData,
    handleCellEdit,
    saveChanges,
    discardChanges,
    resetData,
    startEditing,
    cancelEditing,
    proceedWithEditing,
    cancelRejectedEdit,
    isFileEditing,
    isAnyFileEditing,
    isCellEdited,
    getFileStatus,
  };
};
